import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { apiCall } from "./utils/ApiUtils";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ToastProvider } from "./components/common/Toast";
import { ConfirmProvider, useConfirm } from "./components/common/ConfirmDialog";
import { PromptProvider } from "./components/common/PromptDialog";
import Login from "./components/Login";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import GlobalSearch from "./components/common/GlobalSearch";
import HospitalSelector from "./components/HospitalSelector";

// Dashboard is the most-visited landing tab — kept eager so it paints
// instantly. (The initial tab is "items"; see activeTab below.)
import Dashboard from "./components/Dashboard";
import AppTabNav from "./components/AppTabNav";

// Other tabs are code-split: their chunks load on first visit via React.lazy.
// The visited-tabs pattern keeps mounted tabs alive; lazy just defers the
// chunk download until the tab is first opened.
const CurrentInventory = lazy(() => import("./components/CurrentInventory"));
const ItemManagement = lazy(() => import("./components/ItemManagement"));
const ReorderAnalysis = lazy(() => import("./components/ReorderAnalysis"));
const Configuration = lazy(() => import("./components/Configuration"));
const EquipmentMaintenance = lazy(() => import("./components/EquipmentMaintenance"));
const ImageScan = lazy(() => import("./components/ImageScan"));
const Hospitals = lazy(() => import("./components/Hospitals"));
const UserProfile = lazy(() => import("./components/UserProfile"));

const TabFallback = () => (
  <div className="loading" role="status" aria-live="polite">Loading…</div>
);

// Public password-recovery screens — rendered outside AuthProvider so they
// work without a session. Mirrors quizz113's standalone reset-password.html.
const renderPublicRoute = () => {
  const pathname = (typeof window !== 'undefined' ? window.location.pathname : '');
  if (pathname === '/forgot-password') return <ForgotPassword />;
  if (pathname === '/reset-password') return <ResetPassword />;
  return null;
};

// Main App Component
const AppContent = () => {
  const { user, loading: authLoading, logout, checkAuth, activeHospitalId, isGlobalAdmin, hospitals, setBeforeSwitch } = useAuth();
  const confirm = useConfirm();
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const saved = localStorage.getItem("activeTab");
      const VALID_TABS = ["dashboard", "inventory", "items", "reorder", "config", "equipment", "scan", "hospitals"];
      return VALID_TABS.includes(saved) ? saved : "items";
    } catch { return "items"; }
  });
  const [visitedTabs, setVisitedTabs] = useState(() => new Set([(() => {
    try {
      const saved = localStorage.getItem("activeTab");
      const VALID_TABS = ["dashboard", "inventory", "items", "reorder", "config", "equipment", "scan", "hospitals"];
      return VALID_TABS.includes(saved) ? saved : "items";
    } catch { return "items"; }
  })()]));
  const [items, setItems] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [reorderData, setReorderData] = useState([]);
  const [config, setConfig] = useState({});
  const [systemConfig, setSystemConfig] = useState({});
  const [dataLoaded, setDataLoaded] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [navSearch, setNavSearch] = useState(null);
  const [showProfile, setShowProfile] = useState(false);
  const [userProfile, setUserProfile] = useState(user);
  // Fresh-login tab reset: AuthContext.login() sets the 'lab_fresh_login'
  // sessionStorage flag on success. Only that flag (read once on mount)
  // resets the tab to Item Management — a plain page refresh must NOT
  // reset the persisted tab, so we can't key off user going null → user.
  useEffect(() => {
    let fresh = false;
    try { fresh = sessionStorage.getItem("lab_fresh_login") === "1"; } catch {}
    if (fresh) {
      try { sessionStorage.removeItem("lab_fresh_login"); } catch {}
      setActiveTab("items");
      setVisitedTabs(new Set(["items"]));
      try { localStorage.setItem("activeTab", "items"); } catch {}
    }
  }, []);

  // A persisted tab may not be valid for the user who logs in next — e.g. a
  // global admin leaves "hospitals" active, then a per-hospital user logs in
  // on the same browser and would see a blank main area. Reset to a safe
  // default when the persisted tab requires permissions they don't have.
  useEffect(() => {
    if (!user) return;
    if (activeTab === "hospitals" && !isGlobalAdmin) {
      setActiveTab("items");
      setVisitedTabs((prev) => {
        const next = new Set(prev);
        next.delete("hospitals");
        return next;
      });
      try { localStorage.setItem("activeTab", "items"); } catch {}
    }
  }, [user, isGlobalAdmin, activeTab]);
  const [configFocusSection, setConfigFocusSection] = useState(null);
  // Dirty-form tracking: components call onDirtyChange(true) when they have
  // unsaved changes, onDirtyChange(false) after saving. Tab switches warn
  // the user if a form is dirty so they don't lose data.
  const dirtyRef = useRef(false);
  const onDirtyChange = (isDirty) => { dirtyRef.current = isDirty; };

  // Warn on page close/refresh when a form is dirty
  useEffect(() => {
    const handler = (e) => {
      if (dirtyRef.current) { e.preventDefault(); e.returnValue = ""; }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  // Register the dirty-form guard with AuthContext so hospital switches and
  // logout warn about unsaved changes too (not just tab switches). The guard
  // returns false when the user declines to discard — switch/logout abort.
  useEffect(() => {
    setBeforeSwitch(async () => {
      if (!dirtyRef.current) return true;
      const confirmed = await confirm({
        title: "Unsaved Changes",
        message: "You have unsaved changes. Leave without saving?",
        confirmLabel: "Leave without saving",
        cancelLabel: "Stay",
        variant: "danger",
      });
      if (!confirmed) return false;
      dirtyRef.current = false;
      return true;
    });
  }, [setBeforeSwitch, confirm]);

  // Race guard: each fetchData call increments this counter; only the most
  // recent call's results are applied to state. Prevents a slow fetch for
  // hospital A from overwriting state after a faster fetch for hospital B.
  const fetchSeqRef = useRef(0);

  const fetchData = async () => {
    const seq = ++fetchSeqRef.current;
    try {
      const [itemsRes, inventoryRes, reorderRes, configRes, systemConfigRes] =
        await Promise.all([
          apiCall("/items"),
          apiCall("/inventory"),
          apiCall("/reorder-analysis"),
          apiCall("/config"),
          apiCall("/system-config"),
        ]);

      if (seq !== fetchSeqRef.current) return; // a newer fetch superseded this one
      setItems(itemsRes);
      setInventory(inventoryRes);
      setReorderData(reorderRes);
      setConfig(configRes);
      setSystemConfig(systemConfigRes);
      setDataLoaded(true);
      setLoadError(null);
    } catch (error) {
      if (seq !== fetchSeqRef.current) return;
      // A dead session is handled globally: apiCall broadcasts 'auth:expired'
      // and AuthProvider drops the user, which unmounts this component. Just
      // stop here rather than setting a load error the user will never see.
      if (error?.status === 401) return;
      // Surface the error so the user isn't stuck on a blank "Loading..." screen.
      setLoadError(error.message || "Failed to load data. Please check your connection and try again.");
      setDataLoaded(true); // allow the UI to render so the retry button is reachable
    }
  };

  useEffect(() => {
    if (user) {
      setDataLoaded(false);
      fetchData();
    }
  }, [user, activeHospitalId]);

  useEffect(() => {
    try { localStorage.setItem("activeTab", activeTab); } catch {}
  }, [activeTab]);

  const handleTabChange = async (tabName, searchTerm) => {
    if (dirtyRef.current && tabName !== activeTab) {
      const confirmed = await confirm({
        title: "Unsaved Changes",
        message: "You have unsaved changes. Leave without saving?",
        confirmLabel: "Leave without saving",
        cancelLabel: "Stay",
        variant: "danger",
      });
      if (!confirmed) return;
      dirtyRef.current = false;
    }
    setActiveTab(tabName);
    setVisitedTabs((prev) => new Set(prev).add(tabName));
    if (searchTerm) {
      setNavSearch({ tab: tabName, term: searchTerm, ts: Date.now() });
    }
  };

  if (authLoading) {
    return <div className="loading">Loading...</div>;
  }

  if (!user) {
    return <Login />;
  }

  if (!dataLoaded) {
    return <div className="loading">Loading inventory data...</div>;
  }

  if (loadError) {
    return (
      <div>
        {/* Render the header so the user can switch hospitals (the saved one
            may be the reason for the failure) or log out — not just Retry. */}
        <header className="app-header">
          <span className="app-header-title">Lab Inventory</span>
          <HospitalSelector />
          <span className="app-header-user">
            <button onClick={logout} className="btn btn-danger btn-sm">
              Logout
            </button>
          </span>
        </header>
        <div className="app-error-state">
          <div className="info-box info-box--red" style={{ maxWidth: 480, margin: "0 auto 1rem" }}>
            <strong>Couldn't load data.</strong>
            <p style={{ margin: "0.5rem 0 0" }}>{loadError}</p>
          </div>
          <button className="btn btn-primary" onClick={() => { setLoadError(null); setDataLoaded(false); fetchData(); }}>
            <i className="fas fa-redo" style={{ marginRight: "0.4rem" }}></i> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="sr-only">Lab Inventory</h1>
      {/* Header with user info and logout */}
      <header className="app-header">
        <span className="app-header-title">Lab Inventory</span>
        <HospitalSelector />
        {!isGlobalAdmin && activeHospitalId && (
          <span className="hospital-badge">
            <i className="fas fa-hospital" style={{ color: 'var(--brand-primary)' }} aria-hidden="true"></i>
            {user.hospitalName || hospitals.find(h => h.id === activeHospitalId)?.name || activeHospitalId}
          </span>
        )}
        <GlobalSearch items={items} inventory={inventory} activeTab={activeTab} onTabChange={handleTabChange} />
        <span className="app-header-user">
          <button
            onClick={() => { setUserProfile(user); setShowProfile(true); }}
            className="btn btn-link"
            style={{ padding: 0, color: 'inherit', textDecoration: 'none', cursor: 'pointer', background: 'none', border: 'none' }}
            title="Click to edit profile / change password"
            aria-label={`Edit profile: ${user.fullName || user.username}`}
          >
            <i className="fas fa-user-circle" style={{ marginRight: '0.4rem' }} aria-hidden="true"></i>
            {user.fullName || user.username} ({user.role})
          </button>
          <button onClick={logout} className="btn btn-danger btn-sm">
            Logout
          </button>
        </span>
      </header>
      <AppTabNav activeTab={activeTab} onTabChange={handleTabChange} isGlobalAdmin={isGlobalAdmin} />

      {/* Tab Content — visited tabs stay mounted (hidden when inactive) so form
          state is preserved when navigating to Configuration and back.
          Unvisited tabs are not rendered until first visit. */}
      <main>
      <Suspense fallback={<TabFallback />}>
      {(activeTab === "dashboard" || visitedTabs.has("dashboard")) && (
        <div style={{ display: activeTab === "dashboard" ? "block" : "none" }}>
          <Dashboard
            items={items}
            inventory={inventory}
            config={config}
            onTabChange={handleTabChange}
            apiCall={apiCall}
            activeHospitalId={activeHospitalId}
          />
        </div>
      )}
      {(activeTab === "inventory" || visitedTabs.has("inventory")) && (
        <div style={{ display: activeTab === "inventory" ? "block" : "none" }}>
          <CurrentInventory
            inventory={inventory}
            items={items}
            config={config}
            onUpdateStock={fetchData}
            apiCall={apiCall}
            navSearch={navSearch}
            activeHospitalId={activeHospitalId}
          />
        </div>
      )}
      {(activeTab === "items" || visitedTabs.has("items")) && (
        <div style={{ display: activeTab === "items" ? "block" : "none" }}>
          <ItemManagement items={items} inventory={inventory} onRefresh={fetchData} apiCall={apiCall} systemConfig={systemConfig} navSearch={navSearch} activeHospitalId={activeHospitalId} onDirtyChange={onDirtyChange} onGoToConfig={(section) => { setConfigFocusSection(section || null); handleTabChange("config"); }} />
        </div>
      )}
      {(activeTab === "reorder" || visitedTabs.has("reorder")) && (
        <div style={{ display: activeTab === "reorder" ? "block" : "none" }}>
          <ReorderAnalysis
            reorderData={reorderData}
            items={items}
            inventory={inventory}
            config={config}
            systemConfig={systemConfig}
            apiCall={apiCall}
            onRefresh={fetchData}
            activeHospitalId={activeHospitalId}
          />
        </div>
      )}
      {(activeTab === "config" || visitedTabs.has("config")) && (
        <div style={{ display: activeTab === "config" ? "block" : "none" }}>
          <Configuration config={config} systemConfig={systemConfig} items={items} onUpdate={fetchData} apiCall={apiCall} onDataUpdate={fetchData} activeHospitalId={activeHospitalId} focusSection={configFocusSection} onSectionFocused={() => setConfigFocusSection(null)} onDirtyChange={onDirtyChange} onBackToItems={() => handleTabChange("items")} />
        </div>
      )}
      {(activeTab === "equipment" || visitedTabs.has("equipment")) && (
        <div style={{ display: activeTab === "equipment" ? "block" : "none" }}>
          <EquipmentMaintenance activeHospitalId={activeHospitalId} config={config} />
        </div>
      )}
      {(activeTab === "scan" || visitedTabs.has("scan")) && (
        <div style={{ display: activeTab === "scan" ? "block" : "none" }}>
          <ImageScan systemConfig={systemConfig} onRefresh={fetchData} activeHospitalId={activeHospitalId} />
        </div>
      )}
      {isGlobalAdmin && (activeTab === "hospitals" || visitedTabs.has("hospitals")) && (
        <div style={{ display: activeTab === "hospitals" ? "block" : "none" }}>
          <Hospitals apiCall={apiCall} />
        </div>
      )}
      </Suspense>
      </main>

      {showProfile && (
        <Suspense fallback={<TabFallback />}>
        <UserProfile
          user={userProfile}
          onClose={() => setShowProfile(false)}
          onUpdateUser={(u) => { setUserProfile(u); if (checkAuth) checkAuth(); }}
        />
        </Suspense>
      )}
    </div>
  );
};

// App wrapper provides auth context — unless a public password-recovery
// route is active, in which case we skip the auth bootstrap entirely.
const App = () => {
  const publicRoute = renderPublicRoute();
  if (publicRoute) return publicRoute;
  return (
    <AuthProvider>
      <ToastProvider>
        <ConfirmProvider>
          <PromptProvider>
            <AppContent />
          </PromptProvider>
        </ConfirmProvider>
      </ToastProvider>
    </AuthProvider>
  );
};

// Initialize the React app
const container = document.getElementById("app");
const root = createRoot(container);
root.render(<App />);
